/*
 * Copyright (c) 2008-2025 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/* global log */

import Vue from "vue"
import api from "@/config/api"
import { MEETING_KEYS_TO_LOAD, TENANT_KEYS_TO_LOAD } from "../config/inheritableConfigConstants"
import store from "./index"
import { EventBus } from "~/common/src/event-bus" // or simply 'event-bus' (but this is not detected by IDEA)

/**
 * Listen on meeting:connectFinished event which is triggered when the MCC has finished its high-level handshake in
 * order to load all required inheritable meeting data from the backend.
 * The event meeting:connectFinished is triggered when the InteractionsMessage was received from the backend.
 */
EventBus.$on("meeting:connectFinished", (meetingId) => {
  if (meetingId) {
    store.dispatch("inheritableConfig/loadInheritableMeetingData", {
      meetingId,
    })
  }
})

export default {
  namespaced: true,

  state: {
    // (objectType, objectId, key) -> value
    directValues: {},
    effectiveValues: {},
  },

  mutations: {
    setDirectValue(state, { objectType, objectId, key, value }) {
      // Ensure objectType exists
      if (!state.directValues[objectType]) {
        Vue.set(state.directValues, objectType, {})
      }

      // Ensure objectId exists
      if (!state.directValues[objectType][objectId]) {
        Vue.set(state.directValues[objectType], objectId, {})
      }

      // Set the actual key
      Vue.set(state.directValues[objectType][objectId], key, value)
    },

    setEffectiveValue(state, { objectType, objectId, key, value }) {
      if (!state.effectiveValues[objectType]) {
        Vue.set(state.effectiveValues, objectType, {})
      }

      if (!state.effectiveValues[objectType][objectId]) {
        Vue.set(state.effectiveValues[objectType], objectId, {})
      }

      Vue.set(state.effectiveValues[objectType][objectId], key, value)
    },
  },

  actions: {
    loadInheritableMeetingData({ dispatch, rootGetters }, { meetingId }) {
      MEETING_KEYS_TO_LOAD.forEach((k) => {
        dispatch("loadValue", {
          objectType: "MeetingModel",
          objectId: meetingId,
          key: k,
        })
      })
      const meeting = rootGetters["meetings/byId"](meetingId)
      const tenantId = meeting?.tenantId
      if (!tenantId) {
        log.error("No tenantId found in meeting " + meetingId + " or meeting not yet loaded")
        return
      }
      TENANT_KEYS_TO_LOAD.forEach((k) => {
        dispatch("loadValue", {
          objectType: "Tenant",
          objectId: tenantId,
          key: k,
        })
      })

      if (meeting.parentId) {
        // It's a breakout meeting - let's load the data for the parent as well. Note: This may lead to duplicate load once we navigate to the main meeting, but better
        // that than missing crucial data
        store.dispatch("inheritableConfig/loadInheritableMeetingData", {
          meetingId: meeting.parentId,
        })
      }
    },

    /**
     * Loads a value from backend if not already present in state.
     * @param {Object} context Vuex context
     * @param {Object} params
     * @param {string} params.objectType
     * @param {string|number} params.objectId
     * @param {string} params.key
     */
    async loadValue({ commit }, { objectType, objectId, key }) {
      try {
        // REST call (replace with axios if preferred)
        let promise = Vue.http.get(api.INHERITABLE_CONFIG_VALUE(objectType, objectId, key))

        const response = await promise
        if (!response.ok) {
          log.error(`Error loading value: ${response.status}`)
          return null
        }
        const data = await response.json()

        // Commit mutations
        commit("setDirectValue", {
          objectType,
          objectId,
          key,
          // Pending DEV-3396 workaround for not marshalled nulls - remove when fixed in backend
          value: data.directValue ?? null,
        })
        commit("setEffectiveValue", {
          objectType,
          objectId,
          key,
          // Pending DEV-3396 workaround for not marshalled nulls - remove when fixed in backend
          value: data.effectiveValue ?? null,
        })

        return data
      } catch (error) {
        log.error(`Error loading value:`, error)
        return null
      }
    },

    /**
     * Writes a value to the backend and updates local store on success.
     */
    async saveDirectValue({ commit }, { objectType, objectId, key, value }) {
      try {
        // REST call
        const url = api.INHERITABLE_CONFIG_VALUE(objectType, objectId, key)
        let promise
        if (value === null || value === undefined) {
          promise = Vue.http.delete(url)
        } else {
          promise = Vue.http.put(url, JSON.stringify({ value }), { "Content-Type": "application/json" })
        }

        const response = await promise

        if (!response.ok) {
          log.error(`Error writing direct value: ${response.status}`)
          return false
        }

        // Get updated values
        const data = await response.json()

        // Commit mutations
        commit("setDirectValue", {
          objectType,
          objectId,
          key,
          value: data.directValue,
        })
        commit("setEffectiveValue", {
          objectType,
          objectId,
          key,
          value: data.effectiveValue,
        })

        return true
      } catch (error) {
        log.error(`Error saving direct value:`, error)
        return false
      }
    },
  },

  getters: {
    getDirectValue: (state) => (objectType, objectId, key) => {
      return state.directValues?.[objectType]?.[objectId]?.[key]
    },

    getEffectiveValue: (state) => (objectType, objectId, key) => {
      return state.effectiveValues?.[objectType]?.[objectId]?.[key]
    },
  },
}
